/* v8 ignore file -- @preserve */

/* eslint-disable jsdoc/require-jsdoc */
// @ts-expect-error there are no TS definitions for this package
import { KROT } from "captcha-eu-sdk";
import {
    createEffect,
    createSignal,
    Match,
    Suspense,
    Switch,
} from "solid-js";
import { render } from "solid-js/web";
import App from "./App";
import { Loader } from "./components/Loader";
import { config } from "./config";
import { translationsLoaded } from "./config/locale";
import { useStageData } from "./hooks/index.js";
import { ThemeProvider } from "./styles";
import "./utilities/polyfill/polyfills";

if (config.captchaPublicKey) {
    KROT.init();
    KROT.setup(config.captchaPublicKey);
    if (config.captchaProxyUrl) {
        KROT.KROT_HOST = config.captchaProxyUrl;
    }
}

const container = document.querySelector("#sso-root");

if (!(container instanceof HTMLElement)) {
    throw new Error(
        "Root element not found. Did you forget to add it to your index.html? Or maybe the id attribute got misspelled?",
    );
}

void render(
    () => {
        const { stageData } = useStageData();
        const [currentScale, setCurrentScale] = createSignal(1);
        const [original, setOriginal] = createSignal<null | [typeof Element.prototype.getBoundingClientRect]>(null);

        const reset = (): void => {
            const normal = original()?.[0];
            if (normal) {
                Element.prototype.getBoundingClientRect = normal;
            }
            Object.assign(
                window,
                {
                    innerWidth: document.documentElement.clientWidth,
                    innerHeight: document.documentElement.clientHeight,
                },
            );
            setOriginal(null);
        };

        function apply(scale: number): void {
            if (original()) {
                reset();
            }

            const old = Element.prototype.getBoundingClientRect;
            setOriginal([old]);
            Element.prototype.getBoundingClientRect = function () {
                const rect = old.call(this);

                if (!(this instanceof Element) || !document.body.contains(this)) {
                    console.log("Not inside body:", this, rect);
                    return rect;
                }

                return {
                    ...rect,
                    x: rect.x / scale,
                    y: rect.y / scale,
                    top: rect.top / scale,
                    left: rect.left / scale,
                    right: rect.right / scale,
                    bottom: rect.bottom / scale,
                    width: rect.width / scale,
                    height: rect.height / scale,
                };
            };
            Object.assign(
                window,
                {
                    innerWidth: document.documentElement.clientWidth / scale,
                    innerHeight: document.documentElement.clientHeight / scale,
                },
            );
        }

        createEffect(() => {
            const scale = stageData.latest?.scale ?? 1;
            if (scale === currentScale()) {
                return;
            }
            setCurrentScale(scale);
            if (scale === 1) {
                document.documentElement.style.zoom = "1";
                reset();
                return;
            }

            document.documentElement.style.zoom = scale.toString();
            apply(scale);
            const event = (e: UIEvent): void => {
                if (e.target !== window) {
                    return;
                }
                // Reapply the size scaling on the window.
                Object.assign(
                    window,
                    {
                        innerWidth: document.documentElement.clientWidth / scale,
                        innerHeight: document.documentElement.clientHeight / scale,
                    },
                );
            };
            window.addEventListener("resize", event);
        });

        return (
            <ThemeProvider>
                <Suspense fallback={<Loader dialog noFallbackTitle delay />}>
                    <Switch fallback={<Loader dialog noFallbackTitle delay />}>
                        <Match when={translationsLoaded()}>
                            <App />
                        </Match>
                    </Switch>
                </Suspense>
            </ThemeProvider>
        );
    },
    container,
);
